import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { brand } from "../../../theme/businessFlowsTheme";
import { resetViewRelationship, saveViewRelationship } from "../../../actions";
import EmptyState from "./EmptyState";

const DIRECTIONS = [
  { value: "both", label: "Both ways", help: "Shown from either end of the link." },
  { value: "out", label: "Outgoing", help: "Shown only from the object the link starts at." },
  { value: "in", label: "Incoming", help: "Shown only from the object the link points to." },
];

const smallChip = { height: 20, fontSize: "0.7rem" };

function RelationshipRow({ rel, database, mayManage, apply, askReset }) {
  const [label, setLabel] = useState(rel.label);
  useEffect(() => setLabel(rel.label), [rel.label]);

  const save = (changes, message) => apply(() => saveViewRelationship(database, rel.type, changes), message);
  // A refused label goes back to the saved one.
  const commitLabel = async () => {
    const next = label.trim();
    if (!next) {
      setLabel(rel.label);
      return;
    }
    if (next === rel.label) return;
    const result = await save({ label: next }, `${rel.type} is now called “${next}”.`);
    if (!result?.ok) setLabel(rel.label);
  };
  const direction = DIRECTIONS.find((d) => d.value === rel.direction) || DIRECTIONS[0];

  return (
    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, border: `1px solid ${brand.line}`, bgcolor: rel.inExplorer ? "#fff" : brand.canvas }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {mayManage ? (
            <TextField
              size="small"
              fullWidth={false}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.querySelector("input")?.blur();
              }}
              inputProps={{ maxLength: 60, "aria-label": `Label for ${rel.type}` }}
              sx={{ width: { xs: "100%", sm: 260 } }}
            />
          ) : (
            <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{rel.label}</Typography>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
            {rel.label !== rel.type && (
              <Typography variant="caption" color="text.secondary">
                {rel.type}
              </Typography>
            )}
            <Chip size="small" label={`${rel.count.toLocaleString()} ${rel.count === 1 ? "link" : "links"}`} sx={smallChip} />
            {rel.configured && <Chip size="small" label="Changed" sx={{ ...smallChip, bgcolor: "rgba(240,230,49,0.35)" }} />}
          </Stack>
        </Box>
        {mayManage ? (
          <>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={rel.inExplorer}
                  onChange={(e) =>
                    save({ inExplorer: e.target.checked }, `${rel.label} ${e.target.checked ? "shown in" : "hidden from"} the explorer.`)
                  }
                  inputProps={{ "aria-label": `Show ${rel.label} in the explorer` }}
                />
              }
              label={<Typography variant="caption" sx={{ fontWeight: 600 }}>In explorer</Typography>}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={rel.direction}
              disabled={!rel.inExplorer}
              onChange={(e, value) => {
                if (value && value !== rel.direction) {
                  save({ direction: value }, `${rel.label}: ${DIRECTIONS.find((d) => d.value === value).label.toLowerCase()}.`);
                }
              }}
              aria-label={`Direction of ${rel.label}`}
              sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, fontSize: "0.75rem", px: 1.25, py: 0.25 } }}
            >
              {DIRECTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value} title={option.help}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </>
        ) : (
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
            <Chip
              size="small"
              icon={rel.inExplorer ? <CheckRoundedIcon /> : <CloseRoundedIcon />}
              label={rel.inExplorer ? "In explorer" : "Hidden from explorer"}
              variant={rel.inExplorer ? "filled" : "outlined"}
              sx={{ ...smallChip, fontWeight: 600 }}
            />
            {rel.inExplorer && <Chip size="small" label={direction.label} title={direction.help} sx={{ ...smallChip, fontWeight: 600 }} />}
          </Stack>
        )}
        {rel.configured && mayManage && (
          <Tooltip title="Back to the default">
            <IconButton
              size="small"
              aria-label={`Reset ${rel.label}`}
              onClick={() =>
                askReset({
                  title: `Reset ${rel.label}?`,
                  text: "The explorer follows it both ways again, under its own name.",
                  request: () => resetViewRelationship(database, rel.type),
                  message: `${rel.type} is back to its defaults.`,
                })
              }
            >
              <RestartAltRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
}

// Every relationship type in the database: its name, whether the explorer
// follows it, and in which direction.
export default function RelationshipsTab({ overview, database, mayManage, apply, askReset }) {
  const [query, setQuery] = useState("");
  const relationships = overview.relationships || [];
  const term = query.trim().toLowerCase();
  const filtered = term ? relationships.filter((r) => `${r.label} ${r.type}`.toLowerCase().includes(term)) : relationships;

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        placeholder="Find a relationship"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        inputProps={{ "aria-label": "Find a relationship" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
        sx={{ "& .MuiInputBase-root": { bgcolor: "#fff" } }}
      />
      <Typography variant="body2" color="text.secondary">
        The explorer follows every relationship both ways until you change it. A relationship switched off disappears from the explorer and the
        details panel.
      </Typography>
      {relationships.length === 0 ? (
        <EmptyState
          icon={<HubRoundedIcon />}
          title="No relationships yet"
          text="Objects in this database aren't linked to each other yet. Relationships appear here as soon as content is imported or created."
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<SearchRoundedIcon />} title="No relationships match" text="Try another name, or clear the search." />
      ) : (
        <Stack spacing={1}>
          {filtered.map((rel) => (
            <RelationshipRow key={rel.type} rel={rel} database={database} mayManage={mayManage} apply={apply} askReset={askReset} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
